import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { toJS } from "mobx";

import "../../forms-common.scss";
import { AddUserToOrganization, RemoveUser } from "../../services/authorization/Permissions";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import AddNewButton from "../../components/buttons/add-new-button/AddNewButton";
import { ViewUsersList } from "../../services/authorization/Permissions";
import PageLayout from "../_layout/PageLayout";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { api } from "../../services/api/ApiService";
import { UsersStorageModel } from "../../services/user/UsersStorage";
import StopCollaborationButton from "./components/buttons/StopCollaborationButton";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import DataGrid from "../../components/datatable/DataGrid";
import { UserRolesStorageModel } from "../../services/user/UserRolesStorage";
import { EmploymentStatusesStorageModel } from "../../services/user/EmploymentStatusesStorage";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [userRolesStorage] = useState(() => UserRolesStorageModel.create({}, { api }));

  const [employmentStatusesStorage] = useState(() =>
    EmploymentStatusesStorageModel.create({}, { api })
  );

  const [usersStorage] = useState(() =>
    UsersStorageModel.create(
      { useGridFiltering: true, autoLoad: false, includeDeleted: true },
      { api }
    )
  );

  useEffect(() => {
    async function setStorageFilter() {
      await usersStorage.setContextFilter({
        includeInvited: true,
        includeDeleted: true,
      });
    }
    setStorageFilter();
  }, [usersStorage]);

  useEffect(() => {
    if (userRolesStorage.isLoading || employmentStatusesStorage.isLoading) {
      return;
    }

    usersStorage.setUserRoleOptions(toJS(userRolesStorage.userRoles));
    usersStorage.setEmploymentStatusOptions(toJS(employmentStatusesStorage.employmentStatuses));
  }, [
    employmentStatusesStorage.employmentStatuses,
    employmentStatusesStorage.isLoading,
    userRolesStorage.isLoading,
    userRolesStorage.userRoles,
    usersStorage,
  ]);

  const isAddUserDisabled = useMemo(
    () => !permissionsService.userHasPermissionTo(AddUserToOrganization),
    []
  );

  const isStopCollaborationDisabled = useMemo(
    () =>
      !permissionsService.userHasPermissionTo(RemoveUser) ||
      !usersStorage.anyItemsSelected ||
      !usersStorage.selectedOrganizationMembers?.length,
    [usersStorage.selectedOrganizationMembers, usersStorage.anyItemsSelected]
  );

  const stopCollaborationForSelectedUsers = useCallback(async () => {
    const ids = usersStorage.selectedItems;
    const selectedUsersCount = usersStorage.selectedOrganizationMembers?.length;
    try {
      await usersStorage.stopCollaborationForUsers({ userIds: ids });
      snackbarNotificationsService.showSuccess("UsersPage.remove.success", {
        selectedUsersCount,
      });
    } catch (error) {
      const errorContext = { selectedUsersCount: usersStorage.selectedOrganizationMembers?.length };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.users.remove");
      handleErrorInSnackbar(applicationError);
    }
  }, [usersStorage, handleErrorInSnackbar]);

  return (
    <PageLayout accessWithOneOfThePermissions={[ViewUsersList]} titleKey="general.users">
      <ActionsSection>
        <ButtonsSection>
          <AddNewButton
            onClick={() => history.push("/users/invite-user")}
            disabled={isAddUserDisabled}
            buttonText="InviteUser.title"
          />
          <StopCollaborationButton
            disabled={isStopCollaborationDisabled}
            confirmationAction={stopCollaborationForSelectedUsers}
            confirmationModalTitleKey="UsersPage.remove.confirmation.title"
            confirmationModalTitleContext={{
              selectedUsersCount: usersStorage.selectedOrganizationMembers?.length,
            }}
            confirmationModalSubtitleKey="UsersPage.remove.confirmation"
            fetchLockedInspectionsCountOnDemand
            usersStorage={usersStorage}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.users"
        rowClickPath="/users/:id"
        conditionForRowClickAvailable="isOrganizationMember"
        dataGridStorage={usersStorage}
      />
    </PageLayout>
  );
});
